import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import InputField from '../../components/common/InputField';
import Button from '../../components/common/Button';
import { loginAsync } from '../../redux/slices/authSlice';
import { 
  FaEnvelope, 
  FaLock, 
  FaEye, 
  FaEyeSlash, 
  FaPlane, 
  FaHotel, 
  FaCar, 
  FaUmbrellaBeach,
  FaMapMarkedAlt,
  FaTags,
  FaShieldAlt,
  FaHeadset,
  FaUsers,
  FaGlobe,
  FaHeart
} from 'react-icons/fa';

const LoginPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  const { register, handleSubmit, formState: { errors } } = useForm({
    mode: 'onChange'
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLoginError('');
    try {
      const result = await dispatch(loginAsync(data)).unwrap();
      
      if (result.requiresFaceVerification) {
        toast.info('Additional security verification required');
        navigate('/face-verification', { 
          state: { intendedPath: '/dashboard' }
        });
      } else {
        toast.success(`Welcome back, ${result.user?.name || 'Traveler'}! ✈️`);
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.includes('email') || error.includes('not found')) {
        setLoginError('No account found with this email. Please sign up first.');
        toast.error('Email not registered');
      } else if (error.includes('password')) {
        setLoginError('Incorrect password. Please try again or reset your password.');
        toast.error('Invalid password');
      } else if (error.includes('locked') || error.includes('blocked')) {
        setLoginError('Account temporarily locked. Reset password or contact support.');
        toast.error('Account locked');
      } else if (error.includes('verify')) {
        setLoginError('Please verify your email address first. Check your inbox.');
        toast.error('Email not verified');
      } else {
        setLoginError(error || 'Login failed. Please check your credentials');
        toast.error(error || 'Login failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 m-0 p-0 relative">
      {/* Fixed Background that covers entire viewport and scrolls with content */}
      <div className="fixed inset-0 bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 overflow-hidden">
        {/* Animated waves background */}
        <div className="absolute inset-0 opacity-10">
          <svg className="absolute bottom-0 w-full h-48" preserveAspectRatio="none" viewBox="0 0 1440 120">
            <path fill="white" d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,80C1248,85,1344,75,1392,69.3L1440,64L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
          </svg>
        </div>
        
        {/* Simple Plane Background Pattern - Overlay on gradient */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 transform rotate-12">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute bottom-10 right-10 transform -rotate-45">
            <FaPlane className="text-white text-8xl" />
          </div>
          <div className="absolute top-1/3 right-1/4 transform rotate-90">
            <FaPlane className="text-white text-6xl" />
          </div>
          <div className="absolute bottom-1/3 left-1/4 transform -rotate-12">
            <FaPlane className="text-white text-6xl" />
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="relative z-10 min-h-screen w-full overflow-y-auto">
        {/* Main Content Container with padding for scrolling */}
        <div className="flex items-center justify-center min-h-screen py-8 md:py-12">
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left side - Welcome text */}
              <div className="text-center lg:text-left">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                  ✈️ Your Journey Begins Here
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white">
                  Welcome Back to 
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">
                    Travel Friend
                  </span>
                </h1>
                <p className="text-lg text-blue-100 max-w-xl mx-auto lg:mx-0 mb-6">
                  Sign in to access exclusive deals, manage your bookings, and connect with travelers around the world.
                </p>
                
                {/* Quick Stats */}
                <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaGlobe className="text-yellow-300" />
                    <span className="text-white">100+ Countries</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaUsers className="text-yellow-300" />
                    <span className="text-white">50K+ Travelers</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <FaHeart className="text-yellow-300" />
                    <span className="text-white">10K+ Reviews</span>
                  </div>
                </div>
              </div>

              {/* Right side - Login Form Card */}
              <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-6 transform transition-all duration-500 hover:scale-105">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Sign In</h2>
                    <p className="text-gray-500 text-sm">Access your travel account</p>
                  </div>

                  {/* Error Message */}
                  {loginError && (
                    <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 rounded-lg animate-shake">
                      <p className="text-sm text-red-700">{loginError}</p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                      {/* Email Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <div className="relative">
                          <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="email"
                            placeholder="Enter your email"
                            className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                              errors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                            {...register('email', { 
                              required: 'Email is required',
                              pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address'
                              }
                            })}
                          />
                        </div>
                        {errors.email && (
                          <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                        )}
                      </div>

                      {/* Password Field */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Password
                        </label>
                        <div className="relative">
                          <FaLock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all ${
                              errors.password ? 'border-red-500' : 'border-gray-300'
                            }`}
                            {...register('password', { 
                              required: 'Password is required',
                              minLength: {
                                value: 6,
                                message: 'Password must be at least 6 characters'
                              }
                            })}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>
                        {errors.password && (
                          <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                        )}
                      </div>

                      {/* Forgot Password Link */}
                      <div className="flex justify-end">
                       <Link 
  to="/forgot-password" 
  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
>
  Forgot password?
</Link>
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2 rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
                        isLoading={isLoading}
                        disabled={isLoading || Object.keys(errors).length > 0}
                      >
                        Sign In
                      </Button>
                    </div>
                  </form>

                  {/* Sign Up Link */}
                  <div className="mt-6 text-center">
                    <p className="text-gray-600">
                      New to Travel Friend?{' '}
                      <Link to="/register" className="text-blue-600 hover:text-blue-800 font-semibold hover:underline">
                        Create Account
                      </Link>
                    </p>
                  </div>

                  {/* Trust Badges */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex justify-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaShieldAlt className="text-green-500" /> Secure Login
                      </span>
                      <span className="flex items-center gap-1">
                        <FaHeadset className="text-blue-500" /> 24/7 Support
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;